import React from 'react';
import { Song } from '../../types';

interface QueuePanelProps {
  isOpen: boolean;
  onClose: () => void;
  queue: Song[];
  currentSongId: string | null;
  onPlaySong: (songId: string, options?: { context?: 'library' | 'playlist' | 'queue'; playlistSongs?: Song[]; shuffle?: boolean }) => void;
  onRemoveFromQueue: (songId: string) => void;
  onClearQueue: () => void;
}

export const QueuePanel: React.FC<QueuePanelProps> = ({
  isOpen,
  onClose,
  queue,
  currentSongId,
  onPlaySong,
  onRemoveFromQueue,
  onClearQueue
}) => {
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentIndex = queue.findIndex(s => s.id === currentSongId);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        />
      )}
      
      {/* Sliding Panel */}
      <div 
        className={`fixed right-0 top-0 h-full w-96 bg-gradient-to-b from-purple-700 to-indigo-800 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/20">
            <div>
              <h2 className="text-xl font-bold text-white">Queue</h2>
              <p className="text-sm text-purple-200">
                {queue.length} {queue.length === 1 ? 'song' : 'songs'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Clear Queue Button */}
              {queue.length > 0 && (
                <button
                  onClick={() => {
                    if (confirm(`Clear all ${queue.length} songs from queue?`)) {
                      onClearQueue();
                    }
                  }}
                  className="px-3 py-1.5 text-xs font-medium bg-white/10 hover:bg-white/20 text-white rounded transition-colors flex items-center gap-1"
                  title="Clear queue"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Clear All
                </button>
              )}
              {/* Close Button */}
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                title="Close queue"
              >
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Queue List */}
          <div className="flex-1 overflow-y-auto">
            {queue.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-purple-200">
                <svg className="h-16 w-16 mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
                <p className="text-sm">No songs in queue</p>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {queue.map((song, index) => {
                  const isCurrentSong = song.id === currentSongId;
                  const isUpcoming = index > currentIndex;
                  const isPast = index < currentIndex;

                  return (
                    <div
                      key={`${song.id}-${index}`}
                      className={`p-3 transition-colors group relative ${
                        isCurrentSong
                          ? 'bg-white/20'
                          : isUpcoming
                          ? 'hover:bg-white/5'
                          : 'hover:bg-white/5 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Track Number / Now Playing Indicator */}
                        <div className="w-8 flex-shrink-0 text-center">
                          {isCurrentSong ? (
                            <div className="flex items-center justify-center gap-0.5">
                              <div className="w-1 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
                              <div className="w-1 h-4 bg-white rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
                              <div className="w-1 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
                            </div>
                          ) : (
                            <span className="text-sm text-purple-200 font-medium">{index + 1}</span>
                          )}
                        </div>

                        {/* Song Info */}
                        <div 
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => onPlaySong(song.id, { context: 'queue' })}
                        >
                          <div className={`text-sm font-medium truncate ${isCurrentSong ? 'text-white' : 'text-purple-100'}`}>
                            {song.name}
                          </div>
                          <div className={`text-xs truncate ${isCurrentSong ? 'text-purple-100' : 'text-purple-200'}`}>
                            {song.artist?.trim() || 'Unknown Artist'}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-purple-200">
                            <span>{formatDuration(song.duration)}</span>
                            {isPast && (
                              <span className="text-purple-300">• Played</span>
                            )}
                          </div>
                        </div>

                        {/* Remove Button */}
                        {!isCurrentSong && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemoveFromQueue(song.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-white/10 rounded transition-all"
                            title="Remove from queue"
                          >
                            <svg className="h-4 w-4 text-purple-200 hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer Info */}
          {queue.length > 0 && (
            <div className="p-4 border-t border-white/20 bg-white/5">
              <div className="text-xs text-purple-200">
                Total duration: {formatDuration(queue.reduce((sum, song) => sum + song.duration, 0))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
